import { useEffect, useRef, useState } from 'react';

// Flush every 1.5 seconds for snappier AI generation
const FLUSH_INTERVAL_MS = 1500;
// Sample every 100ms for higher fidelity trajectory
const SAMPLE_RATE_MS = 100;

export function useSpatialTracking(profileId, isPreviewMode = false) {
  const pointsRef = useRef([]);
  const lastSampleRef = useRef(0);
  const timerRef = useRef(null);
  const [generativeLayout, setGenerativeLayout] = useState(null);
  
  useEffect(() => {
    if (isPreviewMode || !profileId) return;

    const flushPoints = async () => {
      const currentPoints = pointsRef.current;
      if (currentPoints.length === 0) return;
      
      pointsRef.current = [];

      try {
        // Send telemetry to the AI Edge Orchestrator
        const edgeUrl = import.meta.env.VITE_SUPABASE_URL + '/functions/v1/generative-ui';
        
        const payload = { 
          profileId, 
          points: currentPoints,
          vw: window.innerWidth,
          vh: Math.max(document.body.scrollHeight, window.innerHeight)
        };

        // Fire-and-forget: persist points to Redis for heatmap analytics
        fetch('/api/v1/spatial-ingest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).catch(() => {}); // Silent fail — analytics should never block UX

        const response = await fetch(edgeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(payload),
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.uiSchema) {
            setGenerativeLayout(data.uiSchema);
          }
        }
      } catch (err) {
        console.warn('[Spatial AI] Failed to trigger Generative UI:', err);
      }
    };

    const handleMouseMove = (e) => {
      const now = Date.now();
      if (now - lastSampleRef.current > SAMPLE_RATE_MS) {
        lastSampleRef.current = now;
        
        // We capture pageX and pageY because we want absolute document coordinates
        // to properly overlay the heatmap later even if the user scrolled.
        pointsRef.current.push({
          x: e.pageX,
          y: e.pageY,
          t: now
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    
    // Flush periodically
    timerRef.current = setInterval(flushPoints, FLUSH_INTERVAL_MS);

    // Flush on unmount/leave
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (timerRef.current) clearInterval(timerRef.current);
      flushPoints();
    };
  }, [profileId, isPreviewMode]);

  return generativeLayout;
}
